import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { each, includes } from 'lodash';
import {
  clearFilters,
  clearFiltersIfEmpty,
  changeAddressFilter,
  changePropertyFilter,
  selectInvestmentType,
  selectPropertyType,
  selectTransactionType
} from '../store/actions/query';
import { delayRetrieveRecords } from '../store/actions/search';
import {
  getSelectedInvestmentTypes,
  getSelectedTransactionTypes,
  getSelectedPropertyTypes,
  getAddressFilter,
  getPropertyFilter
} from '../store/selectors/query';

import { filterLabelMappings, filterKeyMappings } from '../../requirements_constants';

import { formatDate } from 'helpers/formatters/date_time_formats';
import CurrentFilters from 'components/search_page/current_filters';
import Tag from 'components/tags/tag';
import { getInvestmentTypes, getPropertyTypes, getTransactionTypes } from '../store/selectors/static';

export class RequirementsCurrentFiltersGroup extends React.Component {
  static propTypes = {
    addressFilter: PropTypes.object.isRequired,
    clearFiltersIfEmpty: PropTypes.func.isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    investmentTypes: PropTypes.array.isRequired,
    propertyFilter: PropTypes.object.isRequired,
    propertyTypes: PropTypes.array.isRequired,
    selectedInvestmentTypes: PropTypes.array.isRequired,
    selectedPropertyTypes: PropTypes.array.isRequired,
    selectedTransactionTypes: PropTypes.array.isRequired,
    transactionTypes: PropTypes.array.isRequired,

    onAddressFilterChange: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    onInvestmentTypeChange: PropTypes.func.isRequired,
    onPropertyFilterChange: PropTypes.func.isRequired,
    onPropertyTypeChange: PropTypes.func.isRequired,
    onTransactionTypeChange: PropTypes.func.isRequired
  };

  handleFilterChange(callBack, value) {
    const { delayRetrieveRecords } = this.props;

    callBack(value);
    clearFiltersIfEmpty();
    delayRetrieveRecords();
  }

  handleAddressChange = (name) => {
    const { addressFilter, onAddressFilterChange } = this.props;

    addressFilter[name] = null;
    this.handleFilterChange(onAddressFilterChange, addressFilter);
  };

  handleCityChange = (city) => {
    const { addressFilter: { cities, ...filter }, onAddressFilterChange } = this.props;
    const filteredCities = cities.filter(c => c !== city);
    filter["cities"] = filteredCities;
    this.handleFilterChange(onAddressFilterChange, filter);
  }

  handleSelectChange = (onSelectChange, id) => {
    const { delayRetrieveRecords, clearFiltersIfEmpty } = this.props;

    onSelectChange(id, false);
    clearFiltersIfEmpty();
    delayRetrieveRecords();
  };

  handlePropertyFilterChange = (minName, maxName) => {
    const { propertyFilter, onPropertyFilterChange } = this.props;

    propertyFilter[minName] = null;
    if (maxName) {
      propertyFilter[maxName] = null;
    }

    this.handleFilterChange(onPropertyFilterChange, propertyFilter);
  };

  handleClearFilters = () => {
    const { onClearFilters, delayRetrieveRecords } = this.props;

    onClearFilters();
    delayRetrieveRecords();
  }

  isSelected(selected, value) {
    return includes(selected || [], value);
  }

  render() {
    return (
      <CurrentFilters onClearFilters={this.handleClearFilters}>
        {this.renderTags()}
      </CurrentFilters>
    );
  }

  renderTags() {
    const {
      addressFilter,
      investmentTypes,
      propertyTypes,
      propertyFilter,
      selectedInvestmentTypes,
      selectedPropertyTypes,
      transactionTypes,
      selectedTransactionTypes,
      onInvestmentTypeChange,
      onPropertyTypeChange,
      onTransactionTypeChange
    } = this.props;
    const tags = [];


    for (const key in propertyFilter) {
      if (key.includes('Date') && propertyFilter[key]) {
        propertyFilter[key] = formatDate(propertyFilter[key]);
      }
    }

    each(propertyTypes, (type) => {
      if (this.isSelected(selectedPropertyTypes, type.value)) {
        tags.push(
          this.renderTag(
            type.label,
            "Property Type: " + type.label,
            () => this.handleSelectChange(onPropertyTypeChange, type.id)
          )
        );
      }
    });

    each(investmentTypes, (type) => {
      if (this.isSelected(selectedInvestmentTypes, type.value)) {
        tags.push(
          this.renderTag(
            type.label,
            "Investment Type: " + type.label,
            () => this.handleSelectChange(onInvestmentTypeChange, type.id)
          )
        );
      }
    });

    each(transactionTypes, (type) => {
      if (this.isSelected(selectedTransactionTypes, type.value)) {
        tags.push(
          this.renderTag(
            type.label,
            `Transaction type: ${type.label}`,
            () => this.handleSelectChange(onTransactionTypeChange, type.id)
          )
        );
      }
    });

    each(addressFilter.cities, (city) => {
      tags.push(
        this.renderTag(
          city,
          "City: " + city,
          () => this.handleCityChange(city)
        )
      );
    });

    each(Object.keys(filterKeyMappings), (filterKey) => {
      const [minKey, maxKey] = filterKeyMappings[filterKey];
      let filterTags = [];

      if (propertyFilter[minKey] || propertyFilter[maxKey]) {
        filterTags = this.renderFilterTags(
          propertyFilter,
          filterKey,
          minKey,
          maxKey,
          () => this.handlePropertyFilterChange(minKey, maxKey)
        );
      }

      if (addressFilter[minKey] || addressFilter[maxKey]) {
        filterTags = this.renderFilterTags(
          addressFilter,
          filterKey,
          minKey,
          maxKey,
          () => this.handleAddressChange(minKey)
        );
      }

      if (filterTags.length > 0) tags.push(filterTags);
    });

    return tags;
  }

  findLabel = (mappings, key) => {
    return mappings.find(map => map.value  == key).label;
  }

  renderFilterLabel(filterKey, minValue, maxValue = null) {
    const { propertyFilter } = this.props;
    const { buildingSizeUnits, lotSizeUnits } = propertyFilter;

    const values = (minValue && maxValue) ? `${minValue} - ${maxValue}` : (minValue || maxValue);

    if (filterKey == "buildingSize") {
      return `${filterLabelMappings[filterKey]}: ${values} ${filterLabelMappings[buildingSizeUnits || "sf"]}`;
    }

    if (filterKey == "lotSize") {
      return `${filterLabelMappings[filterKey]}: ${values} ${filterLabelMappings[lotSizeUnits || "sf"]}`;
    }

    return `${filterLabelMappings[filterKey]}: ${values}`;
  }

  renderFilterTags(filters, filterKey, minKey, maxKey, onRemove) {
    const tags = [];
    if (maxKey && filters[maxKey]) {
      const minValue = filters[minKey] || null;
      const maxValue = filters[maxKey] || null;
      if (minValue || maxValue) {
        const label = this.renderFilterLabel(filterKey, minValue, maxValue);
        tags.push(
          this.renderTag(filterKey, label, onRemove)
        );
      }
    } else if (filters[minKey]) {
      const value = filters[minKey];
      if (value) {
        const label = this.renderFilterLabel(filterKey, value);
        tags.push(
          this.renderTag(filterKey, label, onRemove)
        );
      }
    }
    return tags;
  }

  renderTag(key, label, onRemove) {
    return (
      <Tag key={key} removable={true} onRemove={onRemove}>
        {label}
      </Tag>
    );
  }
}

const mapStateToProps = state => ({
  addressFilter: getAddressFilter(state),
  investmentTypes: getInvestmentTypes(state),
  propertyFilter: getPropertyFilter(state),
  propertyTypes: getPropertyTypes(state),
  selectedInvestmentTypes: getSelectedInvestmentTypes(state),
  selectedPropertyTypes: getSelectedPropertyTypes(state),
  selectedTransactionTypes: getSelectedTransactionTypes(state),
  transactionTypes: getTransactionTypes(state)
});

const mapDispatchToProps = {
  clearFiltersIfEmpty,
  delayRetrieveRecords,

  onAddressFilterChange: changeAddressFilter,
  onClearFilters: clearFilters,
  onInvestmentTypeChange: selectInvestmentType,
  onPropertyTypeChange: selectPropertyType,
  onPropertyFilterChange: changePropertyFilter,
  onTransactionTypeChange: selectTransactionType
};

export default connect(mapStateToProps, mapDispatchToProps)(RequirementsCurrentFiltersGroup);
